import { useState } from 'react'
import { X } from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { addOrderPlatform, removeOrderPlatform, type OrderPlatformAddressDto, type OrderPlatformMode } from '@/lib/api/v2/planning'
import { ApiError } from '@/lib/api/client'

const PLATFORM_LABEL: Record<OrderPlatformMode, string> = { Tradeplace: 'Tradeplace', Portal: 'Portail' }

interface OrderPlatformsSectionProps {
  platforms: OrderPlatformAddressDto[]
  canManage: boolean
  onChanged: () => void
}

/**
 * « Plateformes de commande » (§ P5): Tradeplace writes on behalf of several suppliers, so its address
 * is on no supplier card — it belongs to the order mode.
 */
export function OrderPlatformsSection({ platforms, canManage, onChanged }: OrderPlatformsSectionProps) {
  const [adding, setAdding] = useState(false)
  const [mode, setMode] = useState<OrderPlatformMode>('Tradeplace')
  const [value, setValue] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  // Locked while saving: success clears the field and closes the form, which must never discard
  // a second address typed in the meantime.
  const add = async () => {
    if (!value.trim() || saving) return
    setSaving(true)
    try {
      await addOrderPlatform(mode, value)
      setValue('')
      setError(null)
      setAdding(false)
      onChanged()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "La plateforme n'a pas pu être ajoutée.")
    } finally {
      setSaving(false)
    }
  }

  const remove = async (id: string) => {
    try {
      await removeOrderPlatform(id)
      onChanged()
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "La plateforme n'a pas pu être retirée.")
    }
  }

  return (
    <section className="space-y-2 rounded-md border p-3" data-testid="order-platforms-section">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-muted-foreground">Plateformes de commande :</span>
        {platforms.length === 0 && <span className="text-muted-foreground">aucune</span>}
        {platforms.map((p) => (
          <Badge key={p.id} variant="outline" className="gap-1 font-normal" data-testid="order-platform-chip">
            {PLATFORM_LABEL[p.mode]} · <span className="font-mono text-xs">{p.value}</span>
            {canManage && (
              <button
                type="button"
                onClick={() => void remove(p.id)}
                className="rounded-full p-0.5 hover:bg-muted"
                aria-label={`Retirer ${p.value}`}
                data-testid="order-platform-remove-btn"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </Badge>
        ))}
        {canManage && !adding && (
          <Button variant="ghost" size="sm" onClick={() => setAdding(true)} data-testid="order-platform-add-btn">
            Ajouter
          </Button>
        )}
      </div>
      {adding && (
        <div className="flex flex-wrap items-center gap-2">
          <Select value={mode} onValueChange={(v) => setMode(v as OrderPlatformMode)}>
            <SelectTrigger className="h-8 w-36" data-testid="order-platform-mode-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Tradeplace">Tradeplace</SelectItem>
              <SelectItem value="Portal">Portail</SelectItem>
            </SelectContent>
          </Select>
          <Input
            value={value}
            onChange={(e) => {
              setValue(e.target.value)
              setError(null)
            }}
            onKeyDown={(e) => e.key === 'Enter' && void add()}
            placeholder="noreply@tradeplace.com"
            className="h-8 w-64"
            disabled={saving}
            data-testid="order-platform-value-input"
          />
          <Button size="sm" onClick={() => void add()} disabled={!value.trim() || saving} data-testid="order-platform-save-btn">
            Ajouter
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setAdding(false)} data-testid="order-platform-cancel-btn">
            Annuler
          </Button>
        </div>
      )}
      {error && <p className="text-sm text-destructive" data-testid="order-platform-error">{error}</p>}
    </section>
  )
}
